import { Component, OnInit } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { ProfileViewerComponent } from '../profile-viewer/profile-viewer.component';
import { environment } from 'environment';
import { BehaviorSubject } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Memory } from 'src/classes/Memories';
import { GlobalService } from '../services/global.service';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { PhotoComment, PhotoCommentsResponse } from 'infrastructure/lib/src/models/photo-comment';
import BaseLevelScene from '../Scenes/BaseLevelScene';
import MainScene from '../Scenes/MainScene';

@Component({
  selector: 'app-memory-modal',
  templateUrl: './memory-modal.component.html',
  styleUrls: ['./memory-modal.component.scss'],
})
export class MemoryModalComponent implements OnInit {
  memoryName: string;
  username: string;
  displayProfileButton: boolean;
  scene: MainScene | BaseLevelScene;
  session: AuthSession;
  public imageSrc: string;
  lastEvaluatedKey: any = null;
  isLoading: boolean = false;
  isLevelPhoto: boolean = false;

  // display the number of likes the photo has
  likes$ = new BehaviorSubject<number>(0);
  myComment$ = new BehaviorSubject<string>("");
  allComments$ = new BehaviorSubject<Array<PhotoComment>>([]);
  description = "";
  previousDescription = "";
  descriptionEnabled= false;
  protected GlobalService = GlobalService;

  constructor(private modalCtrl: ModalController, private http: HttpClient, private toastController: ToastController, public globalService: GlobalService, public authenticator: AuthenticatorService) { }

  async ngOnInit() {
    console.log(`memory name in MemoryModal: ${this.memoryName}`);
    this.session = await fetchAuthSession();
    console.log('scene:');
    console.log(this.scene);

    // Check if this is a "level" photo
    this.isLevelPhoto = this.memoryName.startsWith('public/');

    this.isLoading = false;

    // get the image source signed URL from s3
    const s3ImageSignedURL = await fetch(`${environment.REST_API}get-signed-url?bucket=st-images-bucket&key=${this.memoryName}`);
    console.log("s3ImageSignedURL:", s3ImageSignedURL);

    // extract the username from the memory name
    this.username = this.memoryName.split('/')[0];

    // get the image source signed URL from s3 response
    this.imageSrc = (await s3ImageSignedURL.json()).signedUrl!.toString();
    console.log("imageSrc:", this.imageSrc);

    // retrieve the current number of likes this memory has
    this.http.get(`${environment.REST_API}/metadata?s3Path=${this.memoryName}`).subscribe({
      next: data => {
          this.description = data["description"];
          this.likes$.next(data["likes"]);
      },
      error: error => {
          console.error('There was an error retrieving the photo likes!', error);
      }
    });

    // retrieve the comments for this photo.
    this.loadInitialComments();

  }

  // Close the modal
  cancel() {
    console.log("memory-modal cancel clicked");
    if(this.scene){
      const activeScenes = this.scene.scene.manager.getScenes(true) as Array<BaseLevelScene>;
      activeScenes.forEach((scene) => {
        scene.scene.resume();
      });
    }
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  // Handle infinite scroll
  async loadMore(event: any) {
    if (this.isLoading || !this.lastEvaluatedKey) {
      event.target.complete();
      if (!this.lastEvaluatedKey) {
        event.target.disabled = true;
      }
      return;
    }

    try {
      const result = await this.getPhotoComments();
      event.target.complete();

      // Disable infinite scroll if no more data
      if (!result.hasMore) {
        event.target.disabled = true;
      }
    } catch (error) {
      console.error('Error loading more comments:', error);
      event.target.complete();
    }
  }

  // Initial load
  async loadInitialComments() {
    try {
      // Reset pagination
      this.lastEvaluatedKey = null;
      this.allComments$.next([]);

      await this.getPhotoComments(true);
    } catch (error) {
      console.error('Error loading initial comments:', error);
    }
  }

  convertTimestampToDateTime(timestamp: number) {
    // Create a Date object from the timestamp (in milliseconds)
    const date = new Date(timestamp);
  
    // Extract date components
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const day = date.getDate();
  
    // Extract time components
    const hours = date.getHours();
    const minutes = date.getMinutes();
  
    // Format the date and time string
    const formattedDateTime = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  
    return formattedDateTime;
  }


  async openProfileModal() {
    await this.modalCtrl.dismiss();
    this.modalCtrl.create({
      component: ProfileViewerComponent,
      componentProps: { 
        username: this.username,
        scene: this.scene
      }
    }).then((modal) => {
      console.log("Presenting the profile modal");
      modal.present();
      GlobalService.incrementModalCount();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });
  }

  async likePhoto(){
    console.log("filePath: ", this.memoryName);
    const postBody = JSON.stringify({s3Path: this.memoryName});
    // Add to end of post request
    this.http.post(`${environment.REST_API}/like`, postBody).subscribe({
      next: async data => {
          this.likes$.next(data["likes"]);

          const toast = await this.toastController.create({
            message: 'Photo liked!',
            duration: 1500,
            color: 'success',
            position: 'top',
          });
          await toast.present();
      },
      error: async error => {
          console.error('There was an error!', error);
          const toast = await this.toastController.create({
            message: 'Error liking this photo!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  editDescription(){
    this.descriptionEnabled = true;
    this.previousDescription = this.description;
  }

  async updateDescription(){
    console.log("filePath: ", this.memoryName);
    const postBody = JSON.stringify({s3Path: this.memoryName, description: this.description});
    // Add to end of post request
    this.http.patch(`${environment.REST_API}/photos/description`,
      postBody,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
       }
    ).subscribe({
      next: async data => {
          console.log('data:', data);
          this.description = data["description"];
          this.descriptionEnabled = false;
          const toast = await this.toastController.create({
            message: 'Photo description updated!',
            duration: 1500,
            color: 'success',
            position: 'top',
          });
          await toast.present();
      },
      error: async error => {
          console.error('There was an error!', error);
          const toast = await this.toastController.create({
            message: 'Error updating photo description!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  cancelDescription(){
    this.descriptionEnabled = false;
    this.description = this.previousDescription
  }

  async viewProfile(username: string){
    this.modalCtrl.create({
      component: ProfileViewerComponent,
      componentProps: { 
        username: username,
        scene: null
      }
    }).then((modal) => {
      console.log("Presenting the profile modal");
      modal.present();
      GlobalService.incrementModalCount();
      modal.onDidDismiss().then(() => {
        GlobalService.decrementModalCount();
      });
    });

  }

  goToPhotoLocation(){
    const { x, y } = Memory.extractCoordsFromFilename(this.memoryName);
    GlobalService.$playerXY.next({x: x, y: y+100});
  }

  trackNewCommentText(commentText: string){
    this.myComment$.next(commentText);
  }

  submitComment(){
    // submit photo comment to api endpoint
    const postBody = JSON.stringify({s3Path: this.memoryName, comment: this.myComment$.value});
    this.http.post(`${environment.REST_API}/comment`, postBody,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
       }
    ).subscribe({
      next: async data => {
          console.log("Submitted post!");
          console.log(data);
          this.myComment$.next("");

          // Add the new comment to the list of comments.
          this.allComments$.next([
            ...this.allComments$.getValue(),
            data["photoComment"]
          ].sort((a, b) => b.timestamp - a.timestamp));

          const toast = await this.toastController.create({
            message: 'Comment successfully added!',
            duration: 1500,
            color: 'success',
            position: 'top',
          });
          await toast.present();
      },
      error: async error => {
          console.error('There was an error submitting your comment!', error);

          const toast = await this.toastController.create({
            message: 'There was an error submitting your comment!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }

  async getPhotoComments(isInitialLoad = false): Promise<{hasMore: boolean}> {
    try {
      // Build query parameters
      let params = new HttpParams()
        .set('s3Path', this.memoryName);

      if (!isInitialLoad && this.lastEvaluatedKey) {
        params = params.set('lastEvaluatedKey', 
          encodeURIComponent(JSON.stringify(this.lastEvaluatedKey)));
      }

      this.isLoading = true;

      // Convert the subscription to a Promise.
      return new Promise((resolve, reject) => {
        this.http.get(`${environment.REST_API}/comment`, {params})
          .subscribe({
            next: (data: PhotoCommentsResponse) => {
              console.log("Retrieved all photo comments!");
              console.log(data.photoComments);

              console.log("lastEvaluatedKey:", data.lastEvaluatedKey);
              this.lastEvaluatedKey = data.lastEvaluatedKey;

              // Sort the comments
              data.photoComments.forEach((comment: PhotoComment) => {
                comment.timestamp = Number(comment.timestamp);
              });
              const retrievedComments = data.photoComments.sort((a, b) => 
                b.timestamp - a.timestamp
              );

              // Update comments
              if (isInitialLoad) {
                this.allComments$.next(retrievedComments);
              } else {
                // Create a Set of existing comment IDs for efficient lookup
                const existingCommentIds = new Set(
                  this.allComments$.getValue().map(comment => comment.commentId)
                );
                
                // Filter out any duplicates from the new comments
                const newUniqueComments = retrievedComments.filter(
                  comment => !existingCommentIds.has(comment.commentId)
                );
                
                // Combine existing and new unique comments
                this.allComments$.next([
                  ...this.allComments$.getValue(),
                  ...newUniqueComments
                ].sort((a, b) => b.timestamp - a.timestamp));
              }
              
              this.isLoading = false;
              // Resolve with hasMore status after everything is complete
              resolve({ hasMore: !!this.lastEvaluatedKey });
            },
            error: async error => {
              console.error('There was an error retrieving this photos comments!', error);
              const toast = await this.toastController.create({
                message: 'There was an error retrieving this photos comments!',
                duration: 1500,
                color: 'warning',
                position: 'top',
              });
              await toast.present();
              this.isLoading = false;
              reject(error);
            }
          });
      });
    }
    catch(error) {
      console.error("An error occurred retrieving the photo comments", error);
      this.isLoading = false;
      throw error;
    }
  }

  async deleteComment(comment: PhotoComment){
    console.log("Deleting Comment");
    console.log(JSON.stringify(comment));

    this.http.delete(`${environment.REST_API}/comment?s3Path=${comment.s3Path}&commentId=${comment.commentId}`,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${this.session.tokens.idToken}`
        }),
       }
    ).subscribe({
      next: async (data) => {
        console.log("Photo comment deleted!");
        console.log(data);

        const trimmedComments = this.allComments$.getValue();
          this.allComments$.next(trimmedComments.filter((c) => {
            return c.commentId !== comment.commentId
          })
        );

        const toast = await this.toastController.create({
          message: 'Comment successfully deleted!',
          duration: 1500,
          color: 'success',
          position: 'top',
        });
        await toast.present();

      },
      error: async error => {
          console.error('There was an error deleting this comment!', error);
          const toast = await this.toastController.create({
            message: 'There was an error deleting the comment!',
            duration: 1500,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
      }
    });
  }
}
